import { useEffect, useMemo, useState } from "react";
import { 
  Box, 
  Card, 
  Grid, 
  Stack, 
  styled, 
  useTheme,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Collapse,
  IconButton,
  InputAdornment,
  alpha
} from "@mui/material";
import { 
  FilterList, 
  ExpandMore, 
  Refresh, 
  CalendarToday 
} from "@mui/icons-material";
import Chart from "react-apexcharts";
import { H3, H5, H6, Small, Tiny } from "components/Typography";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";

const StyledChart = styled(Chart)(({ theme }) => ({
  "& .apexcharts-tooltip": {
    boxShadow: "none",
  },
  "& .apexcharts-tooltip.apexcharts-theme-light": {
    border: "none",
    color: theme.palette.text.primary,
    borderRadius: 8,
  },
}));

// Styled components for filters
const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'visible',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]': {
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'invert(0)',
    cursor: 'pointer',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const StatusChip = styled(Typography)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  fontSize: '0.75rem',
  backgroundColor: statuscolor ? alpha(statuscolor, 0.1) : alpha(theme.palette.grey[500], 0.1),
  color: statuscolor || theme.palette.grey[700],
  border: `1px solid ${statuscolor ? alpha(statuscolor, 0.2) : alpha(theme.palette.grey[500], 0.2)}`,
  display: 'inline-block',
}));

function formatCurrency(value) {
  if (value == null || isNaN(Number(value))) return "0";
  const num = Number(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
}

function percent(val) {
  if (val == null || isNaN(Number(val))) return "0%";
  return `${Number(val).toFixed(1)}%`;
}

const InfoStat = ({ title, value, subtitle, color = "primary" }) => {
  const theme = useTheme();
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <Stack spacing={0.5}>
        <Tiny color="text.disabled">{title}</Tiny>
        <H3>{value}</H3>
        {subtitle ? (
          <Small color={theme.palette[color]?.main || theme.palette.text.secondary}>{subtitle}</Small>
        ) : null}
      </Stack>
    </Card>
  );
};

const useLastNDays = (n = 7) => {
  return useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (n - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { startDate: fmt(start), endDate: fmt(end) };
  }, [n]);
};

const AnalyticsDashboard = () => {
  const theme = useTheme();
     const { setPageTitle } = usePageTitle();
  useEffect(() => {
        setPageTitle('Dashboard');
    }, [setPageTitle]);
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [orderType, setOrderType] = useState('all'); // 'all', 'normal', 'bulk'
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Last 30 days
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [useDateFilter, setUseDateFilter] = useState(true); // New state to control date filter usage

  const [loading, setLoading] = useState(true);
  const [salesOverview, setSalesOverview] = useState({});
  const [activityMetrics, setActivityMetrics] = useState({ total_revenue: 0, total_activity: 0, activities: [] });
  const [byProduct, setByProduct] = useState({ category_performance: [], category_growth: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [error, setError] = useState("");

  // Build query string with filters
  const buildQueryString = () => {
    if (orderType === 'all') return '';
    return `?type=${orderType}`;
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError("");
    try {
      const qs = buildQueryString();
      const [activityRes, overviewRes, byProductRes, byCategoryRes] = await Promise.all([
        apiService.get(API_ENDPOINTS.GET_farmer_engagement_activity_metrics + qs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_overview + qs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_product + qs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_category + qs),
      ]);
      setSalesOverview(overviewRes?.data || {});

      const metricsCandidates = [
        activityRes?.data?.data,
        activityRes?.data,
        activityRes,
      ].filter(Boolean);

      const pickMetricsPayload = metricsCandidates.find((payload) => {
        if (Array.isArray(payload?.activities) && payload.activities.length > 0) return true;
        return payload?.total_revenue != null || payload?.total_activity != null;
      }) || {};
      const rawActivities = pickMetricsPayload?.activities;
      const metricsActivities = Array.isArray(rawActivities)
        ? rawActivities
        : (rawActivities && typeof rawActivities === "object" ? Object.values(rawActivities) : []);
      const rawTotalRevenue = pickMetricsPayload?.total_revenue ?? 0;
      const derivedTotalActivity = pickMetricsPayload?.total_activity != null
        ? Number(pickMetricsPayload.total_activity || 0)
        : metricsActivities.reduce((sum, item) => sum + Number(item?.value || 0), 0);
      setActivityMetrics({
        total_revenue: Number(rawTotalRevenue || 0),
        total_activity: derivedTotalActivity,
        activities: metricsActivities,
      });

      // category performance/growth comes from category endpoint
      setByProduct({
        category_performance: Array.isArray(byCategoryRes?.data?.category_performance)
          ? byCategoryRes.data.category_performance
          : Array.isArray(byCategoryRes?.category_performance)
            ? byCategoryRes.category_performance
            : [],
        category_growth: Array.isArray(byCategoryRes?.data?.category_growth)
          ? byCategoryRes.data.category_growth
          : Array.isArray(byCategoryRes?.category_growth)
            ? byCategoryRes.category_growth
            : [],
      });
      // top products is an array from top-products endpoint
      setTopProducts(Array.isArray(byProductRes?.data) ? byProductRes.data : []);
    } catch (err) {
      console.error(err);
      setError(typeof err === "string" ? err : err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleApplyFilters = () => {
    fetchAllData();
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleResetFilters = () => {
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    setStartDate(defaultStartDate.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setOrderType('all');
    setUseDateFilter(true); // Reset to use date filter by default
  };

  const handleClearDateFilter = () => {
    setUseDateFilter(false);
    setStartDate('');
    setEndDate('');
  };

  const handleEnableDateFilter = () => {
    setUseDateFilter(true);
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    setStartDate(defaultStartDate.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
  };

  const getOrderTypeLabel = (type) => {
    switch (type) {
      case 'normal': return 'Orders';
      case 'bulk': return 'Bulk Orders';
      case 'all': return 'All Orders';
      default: return 'All Orders';
    }
  };

  const isFiltersActive = () => {
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    return orderType !== 'all' || 
           !useDateFilter ||
           (useDateFilter && (startDate !== defaultStartDate.toISOString().split('T')[0] || 
            endDate !== new Date().toISOString().split('T')[0]));
  };

  const ordersByStatusChart = useMemo(() => {
    const statuses = salesOverview?.orders_by_status || [];
    const labels = statuses.map((s) => s.status);
    const series = statuses.map((s) => Number(s.count || 0));
    return {
      options: {
        chart: { background: "transparent" },
        labels,
        colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.error.main, theme.palette.grey[500], theme.palette.secondary.main],
        legend: { show: true, position: "bottom", fontSize: "13px", fontWeight: 500 },
              theme: { mode: theme.palette.mode },
      tooltip: {
        fillSeriesColor: false,
        theme: 'dark',
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex];
          const label = w.globals.labels[seriesIndex];
          const color = w.config.colors[seriesIndex]; // 👈 get the same color as the slice
          return `
            <div style="
              padding: 6px 10px;
              border-radius: 6px;
              background: ${color};
              color: #fff;
              font-weight: 600;
              font-size: 13px;
              box-shadow: 0 2px 6px rgba(0,0,0,0.25);
            ">
              ${label}: ${value}
            </div>
          `;
        },
      },


        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: { show: true, total: { show: true, label: "Orders" } },
            },
          },
        },
      },
      series,
    };
  }, [salesOverview, theme]);

  const dailyRevenueChart = useMemo(() => {
    const daily = salesOverview?.daily_sales || [];
    const categories = daily.map((d) => d.date);
    const revenue = daily.map((d) => Number(d.revenue || 0));
    const orders = daily.map((d) => Number(d.orders || 0));
    return {
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        colors: [theme.palette.primary.main, theme.palette.info.main],
        xaxis: {
          categories,
          labels: { style: { colors: theme.palette.text.disabled, fontFamily: theme.typography.fontFamily, fontWeight: 500 } },
        },
        yaxis: [{
          labels: { style: { colors: theme.palette.text.secondary } },
          title: { text: "Revenue" },
        }, {
          opposite: true,
          labels: { style: { colors: theme.palette.text.secondary } },
          title: { text: "Orders" },
        }],
        theme: { mode: theme.palette.mode },
        tooltip: { y: { formatter: (val, { seriesIndex }) => seriesIndex === 0 ? formatCurrency(val) : `${val} orders` } },
      },
      series: [
        { name: "Revenue", type: "area", data: revenue },
        { name: "Orders", type: "line", data: orders },
      ],
    };
  }, [salesOverview, theme]);

const categoryRevenueChart = useMemo(() => {
  const DEFAULT_CATEGORY_LIMIT = 10;
  const perf = byProduct?.category_performance || [];
  const activities = Array.isArray(activityMetrics?.activities) ? activityMetrics.activities : [];
  const hasCategoryData = perf.length > 0;
  const limitedPerf = hasCategoryData ? perf.slice(0, DEFAULT_CATEGORY_LIMIT) : [];
  const limitedActivities = hasCategoryData ? [] : activities.slice(0, DEFAULT_CATEGORY_LIMIT);
  const categories = hasCategoryData
    ? limitedPerf.map((p) => p.category || "N/A")
    : limitedActivities.map((a) => a.label || "N/A");
  const revenue = hasCategoryData
    ? limitedPerf.map((p) => Number(p.total_revenue || 0))
    : limitedActivities.map((a) => Number(a.value || 0));
  const quantity = hasCategoryData ? limitedPerf.map((p) => Number(p.total_quantity || 0)) : [];

  return {
    options: {
      chart: {
        background: "transparent",
        toolbar: { show: false },
      },
      stroke: {
        width: [0, 3],
        curve: "smooth",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "45%",
          dataLabels: { position: "top" },
        },
      },
      dataLabels: { enabled: false },
      colors: [theme.palette.primary.main, theme.palette.warning.main],
      grid: {
        borderColor: theme.palette.divider,
        strokeDashArray: 4,
        padding: { left: 10, right: 10 },
      },
      xaxis: {
        categories,
        tickPlacement: "on",
        labels: {
          show: true,
          rotate: -45,
          hideOverlappingLabels: false,
          trim: false,
          style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          formatter: (value) =>
            value && value.length > 15 ? `${value.slice(0, 15)}...` : value,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: hasCategoryData ? [
        {
          title: { text: "Revenue (₹)" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
            formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val),
          },
        },
        {
          opposite: true,
          title: { text: "Quantity" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
      ] : [
        {
          title: { text: "Count" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
      ],
      legend: {
        position: "bottom",
        labels: { colors: theme.palette.text.secondary },
        fontSize: "12px",
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (value, { dataPointIndex }) => {
            // Show full category name in tooltip
            return categories[dataPointIndex] || value;
          },
        },
        y: {
          formatter: (val, { seriesIndex }) =>
            !hasCategoryData ? `${val}` : (seriesIndex === 0 ? formatCurrency(val) : `${val}`),
        },
      },
      theme: { mode: theme.palette.mode },
      responsive: [
        {
          breakpoint: 992,
          options: {
            plotOptions: { bar: { columnWidth: "60%" } },
            xaxis: {
              labels: {
                rotate: -60,
                style: { fontSize: "10px" },
                formatter: (value) =>
                  value && value.length > 10 ? `${value.slice(0, 10)}...` : value,
              },
            },
            legend: { fontSize: "10px" },
          },
        },
        {
          breakpoint: 576,
          options: {
            plotOptions: { bar: { columnWidth: "80%" } },
            xaxis: {
              labels: {
                rotate: -90,
                style: { fontSize: "9px" },
                formatter: (value) =>
                  value && value.length > 8 ? `${value.slice(0, 8)}...` : value,
              },
            },
            legend: { position: "top" },
          },
        },
      ],
    },
    series: hasCategoryData
      ? [
          { name: "Revenue", type: "column", data: revenue },
          { name: "Quantity", type: "line", data: quantity },
        ]
      : [
          { name: "Activity", type: "column", data: revenue },
        ],
  };
}, [byProduct, activityMetrics, theme]);




  const categoryGrowthChart = useMemo(() => {
    const growth = byProduct?.category_growth || [];
    const categories = Array.from(new Set(growth.map((g) => (g.month || "").slice(0, 7))));
    const categoryNames = Array.from(new Set(growth.map((g) => g.product__category__name)));
    const series = categoryNames.map((name) => ({
      name,
      data: categories.map((m) => {
        const row = growth.find((g) => g.product__category__name === name && (g.month || "").startsWith(m));
        return Number(row?.revenue || 0);
      }),
    }));
    return {
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.secondary.main, theme.palette.error.main],
        xaxis: { categories, labels: { style: { colors: theme.palette.text.secondary } } },
        yaxis: { labels: { style: { colors: theme.palette.text.secondary } }, title: { text: "Revenue" } },
        legend: { position: "bottom" },
        theme: { mode: theme.palette.mode },
        tooltip: { y: { formatter: (val) => formatCurrency(val) } },
      },
      series,
    };
  }, [byProduct, theme]);

  return (
    <Box p={2}>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" mb={2} gap={1}>
        <H5>Analytics Dashboard</H5>
        
      </Stack>

      {error ? (
        <Card sx={{ p: 2, mb: 2 }}>
          <Small color="error.main">{error}</Small>
        </Card>
      ) : null}

      {/* Advanced Filters */}
    

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <InfoStat title="Total Revenue" value={formatCurrency(activityMetrics?.total_revenue)} />
        </Grid>
       
        {(Array.isArray(activityMetrics?.activities) ? activityMetrics.activities : []).map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={`${item.label || "activity"}-${idx}`}>
            <InfoStat title={item.label || "Activity"} value={item.value ?? 0} />
          </Grid>
        ))}

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2 }}>
            <H6>Daily Sales</H6>
            <StyledChart type="line" height={300} options={dailyRevenueChart.options} series={dailyRevenueChart.series} />
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, height: "100%" }}>
            <H6>Orders by Status</H6>
            <StyledChart type="donut" height={300} options={ordersByStatusChart.options} series={ordersByStatusChart.series} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <H6>{(byProduct?.category_performance || []).length > 0 ? "Revenue and Quantity by Category" : "Activity by Type"}</H6>
            <StyledChart type="line" height={340} options={categoryRevenueChart.options} series={categoryRevenueChart.series} />
          </Card>
        </Grid>

  <Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <H3 sx={{ mb: 1 }}>Top Products</H3>
    <Box mt={1} sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          minWidth: 720,
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 14,
        }}
      >
        {/* Table Head */}
        <Box
          component="thead"
          sx={{
            bgcolor:
              theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.background.default,
          }}
        >
          <Box
            component="tr"
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {[
              "Product",
              "SKU",
              "Category",
              "Quantity",
              "Revenue",
              "Orders",
            ].map((header, i) => (
              <Box
                key={i}
                component="th"
                sx={{
                  textAlign: "left" ,
                  p: 1.5,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  whiteSpace: "nowrap",
                }}
              >
                {header}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Table Body */}
        <Box component="tbody">
          {(Array.isArray(topProducts) ? topProducts : []).map((row, idx) => (
            <Box
              component="tr"
              key={idx}
              sx={{
                "&:nth-of-type(odd)": {
                  bgcolor:
                    theme.palette.mode === "light"
                      ? theme.palette.action.hover
                      : theme.palette.background.paper,
                },
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === "light"
                      ? theme.palette.action.selected
                      : theme.palette.action.hover,
                  transition: "background-color 0.2s ease-in-out",
                },
              }}
            >
              <Box
                component="td"
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  maxWidth: 160,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={row.product__name}
              >
                {row.product__name}
              </Box>
              <Box
                component="td"
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  fontFamily: "monospace",
                  fontSize: 13,
                  color: theme.palette.text.secondary,
                }}
              >
                {row.product__sku}
              </Box>
              <Box
                component="td"
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  maxWidth: 140,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={row.product__category__name}
              >
                {row.product__category__name}
              </Box>
              <Box
                component="td"
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  color: theme.palette.text.primary,
                }}
              >
                {Number(row.total_quantity || 0)}
              </Box>
              <Box
                component="td"
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  fontWeight: 500,
                  color: theme.palette.success.main,
                }}
              >
                {formatCurrency(row.total_revenue)}
              </Box>
              <Box
                component="td"
                sx={{
                  p: 1.5,
                  textAlign: "left",
                  color: theme.palette.text.secondary,
                }}
              >
                {row.order_count}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Card>
</Grid>

      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard; 